import React, { FC, useEffect } from 'react';
import { Button, Card, Col, Input, Row, Space } from 'antd';
import { styled } from 'styled-components';
import {
  Icon,
  faCheck,
  faXmark,
  faPen,
  faRotateBack,
  faTrashArrowUp,
  faTriangleExclamation,
} from '@/ui/components';
import { useTranslate } from '@/translations';
import { useAppCustomers } from '@/hooks';
import {
  currencyFormatted,
  sectorConverter,
  toTitleCase,
} from '@/utils/helpers';
import { AuthorizationStatus, Damage, DamageAction, DamagePart } from '@/types';
import { DamageImages } from '../DamageImages';
import { DamageActionsGrid, DamageLabourGrid } from '../DamageActions';
import { isIstaDamageLine } from '../isIstaDamageLine';
import {
  editDamageShortcutHint,
  isEditDamageShortcut,
  isTypingTarget,
} from '../preTimingShortcuts';
import {
  getPreTimeProgress,
  PRE_TIME_FR_ICONS,
  PRE_TIME_FR_PARTS_ICONS,
  PRE_TIME_MATERIALS_ICONS,
  PRE_TIME_STATUS_STYLES,
  preTimeStatusLabelKey,
  type PreTimeMetric,
} from '../DamageDataGrid/PreTimeCell';

const isPaintOrFluid = (type?: string) => {
  const normalized = type?.toLowerCase() ?? '';
  return normalized.includes('paint') || normalized.includes('fluid');
};

const actionLineCost = (action: DamageAction, useRetail: boolean) =>
  (useRetail ? action.retailLineCost : action.lineCost) || 0;

const partLineCost = (part: DamagePart, useRetail: boolean) =>
  (useRetail ? part.retailLineCost : part.lineCost) || 0;

export const getDamageCostSummary = (
  actions: DamageAction[] = [],
  useRetail = false,
) => {
  const labourTotal = actions.reduce(
    (sum, action) => sum + actionLineCost(action, useRetail),
    0,
  );

  const allParts = actions.flatMap((action) => action.parts || []);
  const partsTotal = allParts
    .filter((part) => !isPaintOrFluid(part.type))
    .reduce((sum, part) => sum + partLineCost(part, useRetail), 0);
  const paintFluidsTotal = allParts
    .filter((part) => isPaintOrFluid(part.type))
    .reduce((sum, part) => sum + partLineCost(part, useRetail), 0);

  return {
    labourTotal,
    partsTotal,
    paintFluidsTotal,
    damageTotal: labourTotal + partsTotal + paintFluidsTotal,
  };
};

const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: #f0f3fd;
  border-radius: 8px;
`;

const SummaryTitle = styled.div`
  font-weight: 700;
  line-height: 1.3;
`;

const SummaryMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  font-size: 12px;
  color: #595959;
`;

const PreTimeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;
  padding-top: 8px;
  border-top: 1px solid #e6eaf8;
`;

const PreTimeMetrics = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 0;
  min-width: 0;
  color: #434343;
  font-size: 13px;
`;

const PreTimeLabel = styled.span`
  margin-right: 8px;
  color: #8c8c8c;
  font-weight: 600;
`;

const PreTimeStatusTag = styled.span<{ $background: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const PreTimeDivider = styled.span`
  width: 1px;
  height: 16px;
  margin: 0 12px;
  background: #d9d9d9;
`;

const PreTimeMetricItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  color: #434343;

  svg {
    width: 12px;
    height: 12px;
  }

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`;

const MissingPartsChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #ffedd5;
  color: #c2410c;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const CostBar = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  flex-shrink: 0;
  width: 100%;
  padding: 10px 16px;
  border-top: 1px solid #f0f0f0;
`;

const CostItems = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1;
  min-width: 0;
`;

const CostItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
  padding: 0 16px;

  &:first-child {
    padding-left: 0;
  }

  &:not(:last-child) {
    border-right: 1px solid #f0f0f0;
  }
`;

const CostLabel = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.3;
`;

const CostValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  color: #141414;
`;

const CostTotalBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;
  padding: 6px 14px;
  border-radius: 8px;
  background: #e8f3ff;
  color: #0958d9;

  ${CostLabel}, ${CostValue} {
    color: inherit;
  }
`;

const breadcrumbForDamage = (damage: Damage) =>
  [damage.damageAreaDesc, damage.damagePartDesc, damage.damageTypeDesc]
    .map((part) => toTitleCase(part?.trim()))
    .filter(Boolean)
    .join(' > ');

const PreTimeMetricValue: FC<{
  label: string;
  metric: PreTimeMetric;
  icon?: { src: string; label: string };
}> = ({ label, metric, icon }) => (
  <PreTimeMetricItem>
    {`${label}: ${metric.filled}`}
    {icon ? <img src={icon.src} alt={icon.label} title={icon.label} /> : null}
  </PreTimeMetricItem>
);

interface DamageSummaryCardProps {
  damage: Damage;
  canApprove?: boolean;
  canEdit?: boolean;
  onApprove: () => void;
  onReject: () => void;
  onEdit: () => void;
  onRevertAuthorization: () => void;
  onUnDelete: () => void;
  onClose?: () => void;
}

const DamageSummaryCard: FC<DamageSummaryCardProps> = ({
  damage,
  canApprove,
  canEdit,
  onApprove,
  onReject,
  onEdit,
  onRevertAuthorization,
  onUnDelete,
  onClose,
}) => {
  const { t } = useTranslate();
  const progress = getPreTimeProgress(damage);
  const statusStyle = PRE_TIME_STATUS_STYLES[progress.status];
  const canEditDamage =
    !!canEdit && !damage.isDeleted && !damage.authorizationStatusNum;

  useEffect(() => {
    if (!canEditDamage) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!isEditDamageShortcut(event) || isTypingTarget(event.target)) return;
      const modalOpen = Array.from(
        document.querySelectorAll('.ant-modal-wrap'),
      ).some((node) => window.getComputedStyle(node).display !== 'none');
      if (modalOpen) return;

      event.preventDefault();
      onEdit();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canEditDamage, onEdit]);

  const metaItems = [
    { label: t('sector'), value: sectorConverter(damage.sector) },
    { label: t('severity'), value: damage.severityDesc },
    { label: t('repairMethod'), value: damage.repairActionDesc },
    { label: t('workCentre'), value: damage.workCenterDesc },
    { label: t('responsibility'), value: damage.responsibilityDesc },
    { label: t('internalGrade'), value: damage.internalGradingPoints },
  ];

  const renderActions = () => {
    if (damage.isDeleted && canEdit) {
      return (
        <Button
          color="orange"
          variant="outlined"
          icon={<Icon icon={faTrashArrowUp} />}
          onClick={onUnDelete}
        >
          {t('unDelete')}
        </Button>
      );
    }

    if (damage.authorizationStatusNum) {
      return canApprove ? (
        <Button
          color="orange"
          variant="outlined"
          icon={<Icon icon={faRotateBack} />}
          onClick={onRevertAuthorization}
        >
          {t(
            damage.authorizationStatusNum === AuthorizationStatus.Approved
              ? 'unApprove'
              : 'unReject',
          )}
        </Button>
      ) : null;
    }

    return (
      <>
        {canApprove ? (
          <>
            <Button
              color="green"
              variant="outlined"
              icon={<Icon icon={faCheck} />}
              onClick={onApprove}
            >
              {t('approve')}
            </Button>
            <Button
              color="danger"
              variant="outlined"
              icon={<Icon icon={faXmark} />}
              onClick={onReject}
            >
              {t('reject')}
            </Button>
          </>
        ) : null}
        {canEditDamage ? (
          <Button
            color="blue"
            variant="outlined"
            icon={<Icon icon={faPen} />}
            title={editDamageShortcutHint()}
            onClick={onEdit}
          >
            {t('edit')}
          </Button>
        ) : null}
      </>
    );
  };

  return (
    <SummaryBar data-testid="damage-summary-card">
      <div className="d-flex align-items-center justify-content-between w-100">
        <SummaryTitle>{breadcrumbForDamage(damage)}</SummaryTitle>
        <Space>
          {renderActions()}
          {onClose ? (
            <Button
              type="text"
              icon={<Icon icon={faXmark} />}
              onClick={onClose}
            />
          ) : null}
        </Space>
      </div>
      <SummaryMetaRow>
        {metaItems.map((item) => (
          <span key={item.label} className="text-nowrap">
            {item.label}: {item.value || '-'}
          </span>
        ))}
      </SummaryMetaRow>
      <PreTimeRow data-testid="damage-pre-time-summary">
        <PreTimeMetrics>
          <PreTimeLabel>{t('preTime')}:</PreTimeLabel>
          <PreTimeStatusTag
            $background={statusStyle.background}
            $color={statusStyle.color}
          >
            <Icon icon={statusStyle.icon} />
            {t(preTimeStatusLabelKey(progress.status))}
          </PreTimeStatusTag>
          <PreTimeDivider />
          <PreTimeMetricValue
            label={t('frLines')}
            metric={progress.frLines}
            icon={PRE_TIME_FR_ICONS[progress.frIcon]}
          />
          <PreTimeDivider />
          <PreTimeMetricValue
            label={t('parts')}
            metric={progress.parts}
            icon={PRE_TIME_FR_PARTS_ICONS[progress.frPartsIcon]}
          />
          <PreTimeDivider />
          <PreTimeMetricValue
            label={t('materials')}
            metric={progress.materials}
            icon={PRE_TIME_MATERIALS_ICONS[progress.materialsIcon]}
          />
        </PreTimeMetrics>
        {progress.missingParts > 0 ? (
          <MissingPartsChip>
            <Icon icon={faTriangleExclamation} />
            {t('partsMissing')}
          </MissingPartsChip>
        ) : null}
      </PreTimeRow>
    </SummaryBar>
  );
};

const DamageCostSummary: FC<{ actions: DamageAction[] }> = ({ actions }) => {
  const { t } = useTranslate();
  const { hasDamageApproveAccess } = useAppCustomers();
  const totals = getDamageCostSummary(actions, hasDamageApproveAccess);

  const items = [
    { key: 'labour', label: t('totalLabour'), value: totals.labourTotal },
    { key: 'parts', label: t('parts'), value: totals.partsTotal },
    {
      key: 'paintFluids',
      label: t('paintFluids'),
      value: totals.paintFluidsTotal,
    },
  ];

  return (
    <CostBar className="damage-cost-summary" data-testid="damage-cost-summary">
      <CostItems>
        {items.map((item) => (
          <CostItem key={item.key}>
            <CostLabel>{item.label}</CostLabel>
            <CostValue>{currencyFormatted(item.value)}</CostValue>
          </CostItem>
        ))}
      </CostItems>
      <CostTotalBox>
        <CostLabel>{t('damageTotal')}</CostLabel>
        <CostValue>{currencyFormatted(totals.damageTotal)}</CostValue>
      </CostTotalBox>
    </CostBar>
  );
};

interface DamageMasterDetailProps {
  inspectionId: number;
  damageDetailId: number;
  isLabour: boolean;
  actions: DamageAction[];
  canEditFr?: boolean;
  canEditParts?: boolean;
  canDeleteExisting?: boolean;
  canUpdateImage?: boolean;
  note: string;
  authorizationNotes?: string;
  damageLinesLoading?: boolean;
  refetch: () => void;
}

const DamageMasterDetail: FC<DamageMasterDetailProps> = ({
  inspectionId,
  damageDetailId,
  isLabour,
  actions,
  canEditFr,
  canEditParts,
  canDeleteExisting,
  canUpdateImage,
  note,
  authorizationNotes,
  damageLinesLoading,
  refetch,
}) => {
  const { t } = useTranslate();

  return (
    <Row
      gutter={[16, 16]}
      className="damage-detail-container"
      data-testid="damage-master-detail"
    >
      <Col xs={24} xl={16} className="actions-grid-col">
        <div className="actions-grid">
          <div className="actions-grid-container">
            {isLabour ? (
              <DamageLabourGrid
                title={t('labour')}
                canEditFr={canEditFr}
                canEditParts={canEditParts}
                canDeleteExisting={canDeleteExisting}
                inspectionId={inspectionId}
                damageLine={{
                  damageLineId: damageDetailId,
                  dataSource: actions,
                }}
                damageLinesLoading={damageLinesLoading}
                onSubmit={refetch}
              />
            ) : (
              <DamageActionsGrid
                title={t('frCodes')}
                canEditFr={canEditFr}
                canEditParts={canEditParts}
                canDeleteExisting={canDeleteExisting}
                inspectionId={inspectionId}
                damageLine={{
                  damageLineId: damageDetailId,
                  dataSource: actions,
                }}
                damageLinesLoading={damageLinesLoading}
                onSubmit={refetch}
              />
            )}
          </div>
          <DamageCostSummary actions={actions} />
        </div>
      </Col>

      <Col xs={24} xl={8} className="d-flex flex-column gap-3">
        <DamageImages
          damageDetailId={damageDetailId}
          disabled={!canUpdateImage}
        />
        <Card>
          <h6>{t('notes')}</h6>
          <Input.TextArea
            value={note}
            disabled
            className="fw-semibold"
            rows={3}
          />
        </Card>

        {authorizationNotes && (
          <Card>
            <h6>{t('authorizationNotes')}</h6>
            <Input.TextArea
              value={authorizationNotes}
              disabled
              className="fw-semibold"
              rows={3}
            />
          </Card>
        )}
      </Col>
    </Row>
  );
};

interface DamageDetailPanelProps {
  damage: Damage;
  inspectionId: number;
  isLabour: boolean;
  canApprove?: boolean;
  canEditFr?: boolean;
  canEditParts?: boolean;
  canUpdateImage?: boolean;
  onApprove: () => void;
  onReject: () => void;
  onEdit: () => void;
  onRevertAuthorization: () => void;
  onUnDelete: () => void;
  onClose?: () => void;
  damageLinesLoading?: boolean;
  refetch: () => void;
}

export const DamageDetailPanel: FC<DamageDetailPanelProps> = ({
  damage,
  inspectionId,
  isLabour,
  canApprove,
  canEditFr,
  canEditParts,
  canUpdateImage,
  onApprove,
  onReject,
  onEdit,
  onRevertAuthorization,
  onUnDelete,
  onClose,
  damageLinesLoading,
  refetch,
}) => {
  const isIsta = isIstaDamageLine(damage.mechanicalFailure);
  const canEditLineFr = !!canEditFr && !isIsta;
  const canEditLineParts = !!canEditParts && !isIsta;
  const canDeleteIstaAssociations = !!canEditFr && isIsta;

  return (
    <div
      className="d-flex flex-column gap-2 h-100 min-h-0"
      data-testid="damage-detail-panel"
    >
      <DamageSummaryCard
        damage={damage}
        canApprove={canApprove}
        canEdit={canEditLineFr}
        onApprove={onApprove}
        onReject={onReject}
        onEdit={onEdit}
        onRevertAuthorization={onRevertAuthorization}
        onUnDelete={onUnDelete}
        onClose={onClose}
      />
      <div className="damage-split-master-detail">
        <DamageMasterDetail
          inspectionId={inspectionId}
          damageDetailId={damage.id}
          isLabour={isLabour}
          actions={damage.actions}
          canEditFr={canEditLineFr}
          canEditParts={canEditLineParts}
          canDeleteExisting={canDeleteIstaAssociations}
          canUpdateImage={canUpdateImage}
          note={damage.note}
          authorizationNotes={damage.authorizationNotes}
          damageLinesLoading={damageLinesLoading}
          refetch={refetch}
        />
      </div>
    </div>
  );
};
